<template>
    <div>
        <bs5-page-header :title="$t('distributors.title')" :subtitle="$t('distributors.subtitle')">
            <template #actions>
                <bs5-base-button variant="secondary" :loading="loading" @click="fetchDistributors">
                    <i v-if="!loading" class="bi bi-arrow-clockwise"></i>{{ $t("common.refresh") }}
                </bs5-base-button>
                <bs5-base-button variant="primary" @click="openAddDistributorModal">
                    <i class="bi bi-plus-lg"></i>{{ $t("distributors.new") }}
                </bs5-base-button>
            </template>
        </bs5-page-header>

        <div class="au-card">
            <div class="au-card-head">
                <h2 class="au-card-title"><i class="bi bi-shop"></i>{{ $t("distributors.list") }}</h2>
                <div class="au-search ms-auto">
                    <i class="bi bi-search"></i>
                    <input v-model="search" type="search" class="form-control" :placeholder="$t('distributors.search')"
                        :aria-label="$t('distributors.search')" />
                </div>
            </div>

            <div v-if="loading && distributors.length === 0" class="au-empty">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">{{ $t("common.loading") }}</span>
                </div>
            </div>
            <div v-else-if="distributors.length === 0" class="au-empty">
                <i class="bi bi-shop"></i>
                <div class="au-empty-title">{{ $t("distributors.emptyTitle") }}</div>
                <div>{{ $t("distributors.emptyText") }}</div>
            </div>
            <div v-else-if="filteredDistributors.length === 0" class="au-empty">
                <i class="bi bi-search"></i>
                <div>{{ $t("distributors.noResults") }}</div>
            </div>
            <div v-else class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th><i class="bi bi-shop me-1"></i>{{ $t("distributors.distributor") }}</th>
                            <th class="text-end"><i class="bi bi-lightning-charge me-1"></i>{{ $t("common.actions") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="distributor in filteredDistributors" :key="distributor.id as string">
                            <td>
                                <div class="au-entity">
                                    <div class="au-avatar"><i class="bi bi-shop"></i></div>
                                    <div style="min-width: 0;">
                                        <div class="au-entity-name">{{ distributor.name }}</div>
                                        <div class="au-entity-sub">
                                            <i class="bi bi-chat-left-text"></i>
                                            {{ distributor.description || $t("distributors.noDescription") }}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <div class="au-actions">
                                    <bs5-icon-button icon="bi-pencil" :label="$t('common.edit')"
                                        @click="openEditDistributorModal(distributor.id as string)" />
                                    <bs5-icon-button icon="bi-trash3" tone="danger" :label="$t('common.delete')"
                                        @click="deleteDistributor(distributor)" />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="distributors.length > 0 && filteredDistributors.length > 0" class="au-card-foot">
                <span>{{ $t("distributors.total", { count: filteredDistributors.length }) }}</span>
            </div>
        </div>

        <!-- Modale -->
        <EditDistributorModal :visible="showEditDistributorModal" :distributorId="selectedDistributorId as string"
            @close="showEditDistributorModal = false" @distributor-updated="fetchDistributors" />

        <AddDistributorModal :visible="showAddDistributorModal" @close="showAddDistributorModal = false"
            @distributor-added="fetchDistributors" />
    </div>
</template>

<script lang="ts">
import { t } from "@/helpers/i18n";
import { defineComponent, ref } from "vue";
import distributorService from "@/services/distributor.service";
import { Distributor } from "@/models/distributor.model";
import AddDistributorModal from "./modals/AddDistributorModal.vue";
import EditDistributorModal from "./modals/EditDistributorModal.vue";
import { showToast } from "@/utils/toast";

export default defineComponent({
    components: { AddDistributorModal, EditDistributorModal },
    data() {
        return {
            distributors: ref<Distributor[]>([]),
            search: "",
            loading: ref(false),
            showEditDistributorModal: ref(false),
            showAddDistributorModal: ref(false),
            selectedDistributorId: ref<string | null>(null),
        };
    },
    computed: {
        filteredDistributors(): Distributor[] {
            const phrase = this.search.trim().toLocaleLowerCase();
            if (!phrase) {
                return this.distributors;
            }
            return this.distributors.filter(distributor =>
                (distributor.name ?? "").toLocaleLowerCase().includes(phrase)
                || (distributor.description ?? "").toLocaleLowerCase().includes(phrase));
        },
    },
    methods: {
        async fetchDistributors() {
            this.loading = true;
            try {
                const response = await distributorService.getDistributors();
                this.distributors = [...response];
            } catch (error) {
                showToast(t("common.loadFailed"), "error");
                console.error("Error fetching distributors:", error);
            } finally {
                this.loading = false;
            }
        },
        openEditDistributorModal(distributorId: string) {
            this.selectedDistributorId = distributorId;
            this.showEditDistributorModal = true;
        },
        openAddDistributorModal() {
            this.showAddDistributorModal = true;
        },
        async deleteDistributor(distributor: Distributor) {
            const response = await this.$swal.fire({
                title: t("distributors.deleteTitle", { name: distributor.name }),
                text: t("distributors.deleteText"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: t("common.confirmDeleteButton"),
                cancelButtonText: t("common.cancel"),
            });
            if (!response.isConfirmed) {
                return;
            }
            try {
                await distributorService.deleteDistributor(distributor.id as string);
                showToast(t("distributors.deleted"), "success");
            } catch (error) {
                showToast(t("distributors.deleteFailed"), "error");
                console.error("Error deleting distributor:", error);
            } finally {
                this.fetchDistributors();
            }
        },
    },
    mounted() {
        this.fetchDistributors();
    },
});
</script>
